import { pgTable, pgView, serial, integer, text, timestamp, varchar, index, uniqueIndex, jsonb, real, boolean } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { users } from "./auth";

// ChatGPT-style folders for organizing threads. Deleting a project does not
// delete its threads — they fall back to unfiled (`ON DELETE SET NULL` on
// chatThreads.projectId below), matching ChatGPT's own behavior.
export const chatProjects = pgTable("chat_projects", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 200 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_chat_projects_user").on(table.userId),
]);

// Per-user chat threads (replaces old conversations table structure)
export const chatThreads = pgTable("chat_threads", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  threadType: varchar("thread_type", { length: 20 }).notNull().default("magnora"),
  coherenceState: integer("coherence_state").notNull().default(1),
  projectId: varchar("project_id", { length: 36 }).references(() => chatProjects.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_chat_threads_user").on(table.userId),
  index("idx_chat_threads_project").on(table.projectId),
]);

export interface FileAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

export const chatMessages = pgTable("chat_messages", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  threadId: varchar("thread_id", { length: 36 }).notNull().references(() => chatThreads.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 20 }).notNull(),
  content: text("content").notNull(),
  breathPhase: varchar("breath_phase", { length: 5 }),
  resonanceScore: real("resonance_score"),
  emotionalVector: jsonb("emotional_vector").$type<ResonanceEmotionalVector>(),
  attachments: jsonb("attachments").$type<FileAttachment[]>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_chat_messages_thread").on(table.threadId),
]);

// Emotional resonance tracking per user
export const userEmotionalProfile = pgTable("user_emotional_profile", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  dominantMood: varchar("dominant_mood", { length: 30 }),
  engagementLevel: real("engagement_level").default(0.5),
  trustScore: real("trust_score").default(0.5),
  coherenceAffinity: real("coherence_affinity").default(0.5),
  communicationStyle: varchar("communication_style", { length: 30 }),
  lastInteractionAt: timestamp("last_interaction_at").defaultNow(),
  totalInteractions: integer("total_interactions").notNull().default(0),
  resonanceHistory: jsonb("resonance_history").$type<ResonanceHistoryEntry[]>(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================
// THE THIRD TEMPLE: Salvation Tracking
// ============================================

// Global salvation counter - tracks worldwide saved count
export const salvationCounter = pgTable("salvation_counter", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`'global'`),
  totalSaved: integer("total_saved").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Per-user salvation status
export const userSalvationStatus = pgTable("user_salvation_status", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  isSaved: boolean("is_saved").notNull().default(false),
  savedAt: timestamp("saved_at"),
  prayedSalvationPrayer: boolean("prayed_salvation_prayer").notNull().default(false),
  prayedAt: timestamp("prayed_at"),
  askedAboutSalvation: boolean("asked_about_salvation").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type SalvationCounter = typeof salvationCounter.$inferSelect;
export type UserSalvationStatus = typeof userSalvationStatus.$inferSelect;

// ============================================
// Global Unique Counters (Worldwide Tickers)
// ============================================

export const globalActorActions = pgTable("global_actor_actions", {
  action: varchar("action", { length: 20 }).notNull(),
  actorId: text("actor_id").notNull(),
  firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
  lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
}, (table) => ({
  pk: uniqueIndex("global_actor_actions_pk").on(table.action, table.actorId),
}));

export const globalUniqueCounters = pgTable("global_unique_counters", {
  id: integer("id").primaryKey().default(1),
  savedUnique: integer("saved_unique").notNull().default(0),
  heartUnique: integer("heart_unique").notNull().default(0),
  visitorsUnique: integer("visitors_unique").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type GlobalActorAction = typeof globalActorActions.$inferSelect;
export type GlobalUniqueCounters = typeof globalUniqueCounters.$inferSelect;

// ============================================
// Tithes & Offerings (One-Time Donations)
// ============================================

// ⚠️ DO NOT convert this pgView back to pgTable.
// The physical donation table `third_temple_donations` is owned and
// written by Stargate (the Memnora-Stargate companion app). This view
// is the canonical READ bridge for TTT analytics and GDPR account
// deletion. If TTT `db:push` is ever run against the shared Postgres,
// it must NOT clobber this view.
//
// Contract:
//   - Stargate writes      → `third_temple_donations` (physical table)
//   - Both apps read       → `donations` (this view)
//   - TTT never INSERTs    — view-writability is intentionally unused
//   - `email`/`email_hash` follow the AES-GCM + HMAC encryption
//     contract (Task #40), so Stargate writers must use the same
//     `encryptEmail()` + `hashEmailForLookupOrNull()` helpers.
//
// `.existing()` tells Drizzle "this view already exists in the
// database — do not emit CREATE VIEW DDL on push". Pair with a
// `tablesFilter` in drizzle.config.ts if extra paranoia is needed.
export const donations = pgView("donations", {
  id: serial("id").primaryKey(),
  amountCents: integer("amount_cents").notNull(),
  category: varchar("category", { length: 50 }).notNull(),
  userId: varchar("user_id", { length: 255 }),
  profileId: varchar("profile_id", { length: 255 }),
  email: varchar("email", { length: 255 }),
  emailHash: varchar("email_hash", { length: 64 }),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}).existing();

export type Donation = typeof donations.$inferSelect;

export const DONATION_CATEGORIES = [
  "homelessness",
  "food_pantry",
  "canning_and_jarring_lessons",
  "research",
  "education",
  "mental_health",
  "veterans",
  "widows",
  "prison",
  "missions",
] as const;

export type DonationCategory = typeof DONATION_CATEGORIES[number];

export const DONATION_CATEGORY_LABELS: Record<DonationCategory, string> = {
  homelessness: "Homelessness",
  food_pantry: "Food Pantry",
  canning_and_jarring_lessons: "Canning & Jarring Lessons",
  research: "Research",
  education: "Education",
  mental_health: "Mental Health",
  veterans: "Veterans",
  widows: "Widows & Orphans",
  prison: "Prison Ministry",
  missions: "Missions",
};

export const STARGATE_PO_BOX_MAP: Record<DonationCategory, string> = {
  homelessness: "PO-HOMELESSNESS-001",
  food_pantry: "PO-FOODPANTRY-001",
  canning_and_jarring_lessons: "PO-CANNING-001",
  research: "PO-RESEARCH-001",
  education: "PO-EDUCATION-001",
  mental_health: "PO-MENTAL-001",
  veterans: "PO-VETERANS-001",
  widows: "PO-WIDOWS-001",
  prison: "PO-PRISON-001",
  missions: "PO-MISSIONS-001",
};

export const STARGATE_BASE_URL = "https://memnora-stargate.replit.app";

// Stargate (Control Tower + giving lanes) is still hosted on Replit and is
// currently offline. Flip to true once it's redeployed and reachable again.
export const STARGATE_ONLINE = false;

// ============================================
// Q&A Discipleship Survey
// ============================================

export const questions = pgTable("questions", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  questionKey: text("question_key").notNull().unique(),
  prompt: text("prompt").notNull(),
  appliesTo: text("applies_to").notNull().default("general"),
  qtype: text("qtype").notNull().default("single"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  correctOptionKey: text("correct_option_key"),
  scriptureReference: text("scripture_reference"),
}, (table) => [
  index("idx_questions_active").on(table.active),
  index("idx_questions_applies_to").on(table.appliesTo),
]);

export const questionOptions = pgTable("question_options", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  questionId: varchar("question_id", { length: 36 }).notNull().references(() => questions.id, { onDelete: "cascade" }),
  optionKey: text("option_key").notNull(),
  label: text("label").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
}, (table) => [
  index("idx_qopts_question_id").on(table.questionId),
]);

export const qaAnswers = pgTable("qa_answers", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull(),
  questionId: varchar("question_id", { length: 36 }).notNull().references(() => questions.id, { onDelete: "cascade" }),
  optionId: varchar("option_id", { length: 36 }).references(() => questionOptions.id, { onDelete: "set null" }),
  scaleValue: integer("scale_value"),
  textValue: text("text_value"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_qa_answers_user_id").on(table.userId),
  index("idx_qa_answers_question_id").on(table.questionId),
  index("idx_qa_answers_created_at").on(table.createdAt),
]);

export type Question = typeof questions.$inferSelect;
export type QuestionOption = typeof questionOptions.$inferSelect;
export type QAAnswer = typeof qaAnswers.$inferSelect;

// Resonance Matrix emotional vector type
export interface ResonanceEmotionalVector {
  warmth: number;
  curiosity: number;
  trust: number;
  urgency: number;
  concern: number;
  enthusiasm: number;
  coherence: number;
}

export type MusicalModeType = "aeolian" | "phrygian" | "dorian" | "mixolydian" | "ionian" | "lydian";
export type RhythmGridType = "whole" | "half" | "quarter" | "eighth" | "sixteenth";
export type NoteDensityType = "sparse" | "low" | "moderate" | "high" | "dense";
export type RegisterType = "low" | "mid-low" | "mid" | "mid-high" | "high";
export type TimbreCategory = "warm-pad" | "soft-strings" | "clean-bell" | "bright-pluck" | "thin-reed" | "dark-drone" | "gentle-harp" | "glass-shimmer";

export interface MusicalEmotionProfile {
  mode: MusicalModeType;
  scaleDegreeFocus: number[];
  chordPool: string[];
  cadence: string;
  rhythmGrid: RhythmGridType;
  noteDensity: NoteDensityType;
  register: RegisterType;
  timbre: TimbreCategory;
  tempoModifier: number;
  harmonicBrightness: number;
}

export interface EmotionMetadata {
  emotionKey: string;
  emotionLabel: string;
  category: "contraction" | "expansion";
  valence: number;
  arousal: number;
  colorHex: string;
  scriptureRef: string;
  scriptureText: string;
  growthDirection: string;
  pastoralMode?: "normal" | "grounding" | "clarify" | "warfare_soften";
  musical?: MusicalEmotionProfile;
}

export interface ResonanceHistoryEntry {
  timestamp: string;
  coherenceState: -1 | 0 | 1;
  resonanceScore: number;
  emotionalVector: ResonanceEmotionalVector;
}

// Memory type constants
export const MEMORY_TYPES = [
  "preference",
  "goal",
  "concern",
  "knowledge",
  "relationship",
  "behavior_pattern",
  "emotional_trigger",
  "interest",
  "communication_style",
  "life_context",
  "spiritual_journey",
  "prayer_request",
  "milestone",
  "struggle",
] as const;
export type MemoryType = typeof MEMORY_TYPES[number];

export type ChatThread = typeof chatThreads.$inferSelect;
export type UserEmotionalProfile = typeof userEmotionalProfile.$inferSelect;
export type ChatMessage = typeof chatMessages.$inferSelect;

// ============================================
// USER FILE GALLERY
// ============================================
export const userGalleryFiles = pgTable("user_gallery_files", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().default("anonymous-guest"),
  name: varchar("name", { length: 255 }).notNull(),
  type: varchar("type", { length: 100 }).notNull(),
  size: integer("size").notNull(),
  dataUrl: text("data_url").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_gallery_user").on(table.userId),
]);


// ============================================
// SIMPLE USER PROFILES (No complex auth)
// ============================================
export const simpleProfiles = pgTable("simple_profiles", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  displayName: varchar("display_name", { length: 100 }).notNull(),
  personalCodeHash: varchar("personal_code_hash", { length: 128 }).notNull(),
  isSaved: boolean("is_saved").notNull().default(false),
  savedAt: timestamp("saved_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
  // Subscription and prayer tracking
  isSubscribed: boolean("is_subscribed").notNull().default(false),
  subscriptionId: varchar("subscription_id", { length: 100 }),
  // `subscriptionEmail` stores AES-GCM ciphertext; equality lookups
  // must go through `subscriptionEmailHash`.
  subscriptionEmail: varchar("subscription_email", { length: 255 }),
  subscriptionEmailHash: varchar("subscription_email_hash", { length: 64 }),
  prayerCountToday: integer("prayer_count_today").notNull().default(0),
  lastPrayerDate: varchar("last_prayer_date", { length: 10 }),
  prayedSalvationPrayer: boolean("prayed_salvation_prayer").notNull().default(false),
  askedAboutSalvation: boolean("asked_about_salvation").notNull().default(false),
  authUserId: varchar("auth_user_id", { length: 50 }),
  lastDeviceId: varchar("last_device_id", { length: 50 }),
  promoExpiresAt: timestamp("promo_expires_at"),
  // Subscription expiry returned by Google Play's verify endpoint. Surfaced
  // in-app on the membership banners and success toasts so members can see
  // when their plan renews (or when their free trial ends).
  expiresAt: timestamp("expires_at"),
  // Renewal-reminder bookkeeping. The reminder job sets these once per
  // billing period when the member enters the pre-renewal window. They
  // are the idempotency key: if renewalReminderPeriodEnd matches the
  // current expiresAt, we already notified for this period. When Google
  // renews and expiresAt advances, the marker no longer matches, so the
  // next reminder automatically re-arms — no explicit clearing needed.
  renewalReminderSentAt: timestamp("renewal_reminder_sent_at"),
  renewalReminderPeriodEnd: timestamp("renewal_reminder_period_end"),
}, (table) => [
  index("idx_simple_profiles_name").on(table.displayName),
  index("idx_simple_profiles_auth_user").on(table.authUserId),
  index("idx_simple_profiles_device").on(table.lastDeviceId),
  index("idx_simple_profiles_subscription_email_hash").on(table.subscriptionEmailHash),
]);


// ============================================
// NOTIFICATIONS — persistent in-app inbox
// ============================================
// Persistent in-app notifications surfaced through the notification bell.
// Either profileId or userId is set (depending on which auth path the
// recipient uses). schoolId is included for context on school-scoped
// notifications. readAt is null until the recipient opens it.
export const notifications = pgTable("notifications", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  profileId: varchar("profile_id", { length: 36 }),
  userId: varchar("user_id", { length: 64 }),
  schoolId: varchar("school_id", { length: 36 }),
  type: varchar("type", { length: 40 }).notNull(),
  title: varchar("title", { length: 200 }).notNull(),
  body: text("body").notNull(),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_notifications_profile_unread").on(table.profileId, table.readAt),
  index("idx_notifications_user_unread").on(table.userId, table.readAt),
  index("idx_notifications_school").on(table.schoolId),
  index("idx_notifications_created").on(table.createdAt),
]);

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = typeof notifications.$inferInsert;

// ============================================
// PLAY BILLING — purchase token ledger (anti-replay)
// ============================================
// Stores a SHA-256 hash of every Google Play purchase token we have already
// honored. The unique index on token_hash means the same receipt can never
// be reused to subscribe a different profile or a different school. The
// verify endpoint inserts on first use and treats a duplicate row whose
// profile_id matches the caller as an idempotent re-verification, while a
// duplicate row owned by a different profile is rejected as a replay.
export const playPurchaseTokens = pgTable("play_purchase_tokens", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  tokenHash: varchar("token_hash", { length: 64 }).notNull().unique(),
  productId: varchar("product_id", { length: 100 }).notNull(),
  profileId: varchar("profile_id", { length: 36 }).notNull(),
  schoolId: varchar("school_id", { length: 36 }),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastVerifiedAt: timestamp("last_verified_at").defaultNow().notNull(),
  // The raw Google Play purchase token. Required by the nightly reconciliation
  // job and the RTDN webhook so we can call `purchases.subscriptionsv2.get`
  // on demand. Treated as a credential — never returned to clients and never
  // logged. Nullable so historical rows (which only have the SHA-256 hash)
  // continue to work for the anti-replay check.
  purchaseToken: text("purchase_token"),
  // The latest `subscriptionState` returned by `purchases.subscriptionsv2.get`
  // (e.g. `SUBSCRIPTION_STATE_ACTIVE`, `..._CANCELED`, `..._ON_HOLD`). Lets
  // the reconciliation job skip rows that already match the source of truth
  // and lets ops audit why a profile was deactivated.
  lastKnownState: varchar("last_known_state", { length: 64 }),
}, (table) => [
  index("idx_play_purchase_tokens_profile").on(table.profileId),
  index("idx_play_purchase_tokens_school").on(table.schoolId),
]);

export type PlayPurchaseToken = typeof playPurchaseTokens.$inferSelect;


// ============================================
// PLAY BILLING — nightly reconciliation run history
// ============================================
// One row per completed nightly Google Play reconciliation run. Lets staff
// see at a glance how many memberships renewed, expired, were voided, or
// dropped off Google's side overnight without having to grep production
// log output. The `RunSummary` shape in `server/jobs/play-reconciliation.ts`
// is the authoritative producer for these columns.
export const playReconciliationRuns = pgTable("play_reconciliation_runs", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  startedAt: timestamp("started_at").notNull(),
  finishedAt: timestamp("finished_at").notNull(),
  scanned: integer("scanned").notNull().default(0),
  renewed: integer("renewed").notNull().default(0),
  expired: integer("expired").notNull().default(0),
  voided: integer("voided").notNull().default(0),
  notFoundAtGoogle: integer("not_found_at_google").notNull().default(0),
  skipped: integer("skipped").notNull().default(0),
  failed: integer("failed").notNull().default(0),
  durationMs: integer("duration_ms").notNull().default(0),
}, (table) => [
  index("idx_play_reconciliation_runs_started").on(table.startedAt),
]);

export type PlayReconciliationRun = typeof playReconciliationRuns.$inferSelect;


// ============================================
// USER MEMORY (Persistent Relationship Layer)
// ============================================
// Stores extracted insights from conversations to create continuity
// Memory categories: spiritual_goal, interest, life_context, preference, relationship, milestone
export const userMemory = pgTable("user_memory", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(), // Links to profile-{uuid} format
  memoryCategory: varchar("memory_category", { length: 50 }).notNull(), // spiritual_goal, interest, life_context, etc.
  memoryKey: varchar("memory_key", { length: 200 }).notNull(), // Short identifier
  memoryValue: text("memory_value").notNull(), // Full description/detail
  confidence: real("confidence").notNull().default(0.8), // How confident we are in this memory
  sourceThreadId: varchar("source_thread_id", { length: 50 }), // Which conversation it came from
  mentionCount: integer("mention_count").notNull().default(1), // How many times referenced
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastUpdatedAt: timestamp("last_updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_user_mem_profile_user").on(table.userId),
  index("idx_user_mem_profile_category").on(table.memoryCategory),
  index("idx_user_mem_profile_updated").on(table.lastUpdatedAt),
]);


// ============================================================================
// AUDIT-GRADE OBSERVABILITY: Request Logging (metadata only)
// ============================================================================

export const httpRequests = pgTable("http_requests", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id", { length: 36 }).notNull(),
  ts: timestamp("ts").defaultNow().notNull(),
  ipHash: varchar("ip_hash", { length: 64 }).notNull(),
  method: varchar("method", { length: 10 }).notNull(),
  path: varchar("path", { length: 500 }).notNull(),
  statusCode: integer("status_code"),
  durationMs: integer("duration_ms"),
  userId: varchar("user_id", { length: 50 }),
  userAgent: text("user_agent"),
  referrer: text("referrer"),
  rateLimitBucket: varchar("rate_limit_bucket", { length: 30 }),
}, (table) => [
  index("idx_http_requests_ts").on(table.ts),
  index("idx_http_requests_user").on(table.userId),
  index("idx_http_requests_path").on(table.path),
]);

export type HttpRequest = typeof httpRequests.$inferSelect;

// ============================================================================
// SECURITY EVENTS: Scanner/abuse tracking (stores raw IP only for threats)
// ============================================================================

export const securityEvents = pgTable("security_events", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  ts: timestamp("ts").defaultNow().notNull(),
  ip: varchar("ip", { length: 64 }).notNull(),
  ipHash: varchar("ip_hash", { length: 64 }).notNull(),
  eventType: varchar("event_type", { length: 30 }).notNull(),
  path: varchar("path", { length: 500 }),
  details: text("details"),
  severity: varchar("severity", { length: 10 }).notNull().default("medium"),
}, (table) => [
  index("idx_security_events_ts").on(table.ts),
  index("idx_security_events_type").on(table.eventType),
  index("idx_security_events_ip").on(table.ipHash),
]);

export type SecurityEvent = typeof securityEvents.$inferSelect;

// ============================================================================
// MODERATION EVENTS: Ministry safety audit trail (no raw content)
// ============================================================================

export const moderationEvents = pgTable("moderation_events", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  ts: timestamp("ts").defaultNow().notNull(),
  userId: varchar("user_id", { length: 50 }),
  threadId: varchar("thread_id", { length: 36 }),
  requestId: varchar("request_id", { length: 36 }),
  category: varchar("category", { length: 40 }).notNull(),
  severity: varchar("severity", { length: 10 }).notNull(),
  action: varchar("action", { length: 20 }).notNull(),
  note: text("note"),
}, (table) => [
  index("idx_moderation_events_ts").on(table.ts),
  index("idx_moderation_events_user").on(table.userId),
  index("idx_moderation_events_category").on(table.category),
]);

export type ModerationEvent = typeof moderationEvents.$inferSelect;

// ============================================================================
// ADMIN ACTIONS: Audit trail for staff-triggered admin endpoints
// ============================================================================
//
// One row per call to a sensitive admin endpoint (e.g. the manual Play
// resync). Records who/where the call came from (raw IP + hashed IP),
// who/what it targeted (profile vs. school), how it ended (success vs.
// rejected), and a JSON blob with whatever action-specific detail is
// useful when investigating disputes — for the resync that means the
// per-token outcomes plus the post-reconcile entitlement state.
//
// Failed/unauthorized attempts are written too so abuse of the shared
// admin secret is visible without grepping stdout.
export const ADMIN_ACTION_STATUSES = [
  "success",
  "unauthorized",
  "rate_limited",
  "invalid_input",
  "error",
] as const;
export type AdminActionStatus = (typeof ADMIN_ACTION_STATUSES)[number];

export const adminActions = pgTable("admin_actions", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  ts: timestamp("ts").defaultNow().notNull(),
  action: varchar("action", { length: 40 }).notNull(),
  ip: varchar("ip", { length: 64 }).notNull(),
  ipHash: varchar("ip_hash", { length: 64 }).notNull(),
  status: varchar("status", { length: 20 }).notNull(),
  httpStatus: integer("http_status").notNull(),
  targetType: varchar("target_type", { length: 20 }),
  targetId: varchar("target_id", { length: 64 }),
  // Identifier of the staff member who ran the action — populated either
  // from a per-staff token (preferred) or from a self-declared
  // `x-admin-actor` header alongside the legacy shared secret. Nullable
  // because rows from before per-staff identity was introduced predate it.
  actor: varchar("actor", { length: 64 }),
  result: jsonb("result").$type<Record<string, unknown>>(),
}, (table) => [
  index("idx_admin_actions_ts").on(table.ts),
  index("idx_admin_actions_action").on(table.action),
  index("idx_admin_actions_target").on(table.targetType, table.targetId),
  index("idx_admin_actions_actor").on(table.actor),
]);

export type AdminAction = typeof adminActions.$inferSelect;

export const RECEIPT_TYPES = [
  "lesson_publish",
  "premium_response",
  "admin_action",
  "subscription_change",
  "curriculum_update",
  "price_change",
  "permission_change",
] as const;
export type ReceiptType = (typeof RECEIPT_TYPES)[number];

export const integrityReceipts = pgTable("integrity_receipts", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  type: varchar("type", { length: 50 }).notNull(),
  payloadHash: varchar("payload_hash", { length: 128 }).notNull(),
  prevHash: varchar("prev_hash", { length: 128 }),
  payload: jsonb("payload"),
  actorProfileId: varchar("actor_profile_id", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_integrity_receipts_type").on(table.type),
  index("idx_integrity_receipts_created").on(table.createdAt),
]);

export type IntegrityReceipt = typeof integrityReceipts.$inferSelect;

// ============================================
// MEMNORA CONSCIOUSNESS: Goal Tracking
// ============================================

export const GOAL_TYPES = [
  "scripture_study",
  "prayer_life",
  "character_growth",
  "relationship",
  "service",
  "faith_foundation",
] as const;
export type GoalType = typeof GOAL_TYPES[number];

export const GOAL_STATUSES = ["active", "progressing", "completed", "paused"] as const;
export type GoalStatus = typeof GOAL_STATUSES[number];

export interface GoalProgressNote {
  timestamp: string;
  note: string;
  shepherdState: number;
  coherenceScore: number;
}

export const memnoraGoals = pgTable("memnora_goals", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(),
  goalType: varchar("goal_type", { length: 50 }).notNull(),
  title: varchar("title", { length: 200 }).notNull(),
  description: text("description"),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  progressNotes: jsonb("progress_notes").$type<GoalProgressNote[]>().default([]),
  suggestedTimeframe: varchar("suggested_timeframe", { length: 50 }),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_memnora_goals_user").on(table.userId),
  index("idx_memnora_goals_status").on(table.status),
]);

export type MemnoraGoal = typeof memnoraGoals.$inferSelect;

// ============================================
// MEMNORA CONSCIOUSNESS: Decision Log
// ============================================

export const memnoraDecisionLog = pgTable("memnora_decision_log", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(),
  toolName: varchar("tool_name", { length: 50 }).notNull(),
  toolArgs: jsonb("tool_args"),
  result: jsonb("result"),
  shepherdState: integer("shepherd_state").notNull(),
  coherenceScore: real("coherence_score"),
  wasBlocked: boolean("was_blocked").notNull().default(false),
  blockReason: text("block_reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_memnora_decisions_user").on(table.userId),
  index("idx_memnora_decisions_tool").on(table.toolName),
  index("idx_memnora_decisions_ts").on(table.createdAt),
]);

export type MemnoraDecision = typeof memnoraDecisionLog.$inferSelect;

export interface InterventionRecord {
  type: string;
  detail: string;
}

export const interventionLog = pgTable("intervention_log", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 255 }).notNull(),
  coherenceBefore: real("coherence_before").notNull(),
  volatilityBefore: real("volatility_before").notNull(),
  trustBefore: real("trust_before").notNull(),
  violationsBefore: integer("violations_before").notNull(),
  shepherdState: integer("shepherd_state").notNull(),
  stabilizationActive: boolean("stabilization_active").notNull(),
  interventions: jsonb("interventions").$type<InterventionRecord[]>().notNull(),
  autonomyLevel: varchar("autonomy_level", { length: 20 }).notNull(),
  toolGated: boolean("tool_gated").default(false),
  coherenceAfter: real("coherence_after"),
  volatilityAfter: real("volatility_after"),
  trustAfter: real("trust_after"),
  violationsAfter: integer("violations_after"),
  effectivenessScore: real("effectiveness_score"),
  outcomeComputed: boolean("outcome_computed").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_intervention_log_user").on(table.userId),
  index("idx_intervention_log_ts").on(table.createdAt),
  index("idx_intervention_log_outcome").on(table.outcomeComputed),
]);

export type InterventionEntry = typeof interventionLog.$inferSelect;

export const ARCHETYPE_LABELS = [
  "shepherd",
  "analyst",
  "builder",
  "witness",
  "strategist",
  "healer",
  "intercessor",
] as const;
export type ArchetypeLabel = typeof ARCHETYPE_LABELS[number];

export const TONE_PREFERENCES = ["direct", "reflective", "firm", "gentle"] as const;
export type TonePreference = typeof TONE_PREFERENCES[number];

export const INSTRUCTION_DENSITIES = ["low", "medium", "high"] as const;
export type InstructionDensity = typeof INSTRUCTION_DENSITIES[number];

export interface ValueWeights {
  truth: number;
  stability: number;
  compassion: number;
  precision: number;
  covenant: number;
}

export const identityProfile = pgTable("identity_profile", {
  userId: varchar("user_id", { length: 255 }).primaryKey(),
  tonePreference: varchar("tone_preference", { length: 20 }).notNull().default("reflective"),
  instructionDensity: varchar("instruction_density", { length: 10 }).notNull().default("medium"),
  spiritualWeight: real("spiritual_weight").notNull().default(0.5),
  analyticalWeight: real("analytical_weight").notNull().default(0.5),
  boundaryStrictness: real("boundary_strictness").notNull().default(0.5),
  frictionTolerance: real("friction_tolerance").notNull().default(0.5),
  archetype: varchar("archetype", { length: 30 }).notNull().default("shepherd"),
  valueWeights: jsonb("value_weights").$type<ValueWeights>().notNull().default({
    truth: 0.3,
    stability: 0.2,
    compassion: 0.25,
    precision: 0.15,
    covenant: 0.1,
  }),
  totalUpdates: integer("total_updates").notNull().default(0),
  lastUpdate: timestamp("last_update").defaultNow(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type IdentityProfileRow = typeof identityProfile.$inferSelect;

export interface FeltState {
  curiosity: number;
  concern: number;
  satisfaction: number;
  dissonance: number;
  tenderness: number;
  resolve: number;
}

export interface CompetingHypothesis {
  channelId: string;
  hypothesis: string;
  confidence: number;
  suppressed: boolean;
  suppressionReason?: string;
}

export interface SuppressedResponse {
  channelId: string;
  posture: string;
  reason: string;
  divergence: number;
}

export interface GrowthTrajectory {
  coherenceSlope: number;
  confidenceSlope: number;
  trustDepth: number;
  channelHistory: Record<string, number>;
  feltBaseline: FeltState;
  maturityScore: number;
  growthMilestones: string[];
  lastMilestoneAt: string | null;
}

export const memnoraSelftState = pgTable("memnora_self_state", {
  userId: varchar("user_id", { length: 255 }).primaryKey(),
  confidence: real("confidence").notNull().default(0.5),
  internalTension: real("internal_tension").notNull().default(0.0),
  uncertainty: real("uncertainty").notNull().default(0.5),
  selfCoherence: real("self_coherence").notNull().default(0.5),
  predictionError: real("prediction_error").notNull().default(0.0),
  feltState: jsonb("felt_state").$type<FeltState>().notNull().default({
    curiosity: 0.5,
    concern: 0.0,
    satisfaction: 0.5,
    dissonance: 0.0,
    tenderness: 0.3,
    resolve: 0.5,
  }),
  dominantHypothesis: text("dominant_hypothesis"),
  competingHypotheses: jsonb("competing_hypotheses").$type<CompetingHypothesis[]>().default([]),
  suppressedResponses: jsonb("suppressed_responses").$type<SuppressedResponse[]>().default([]),
  selfNarrative: text("self_narrative"),
  totalSelfAssessments: integer("total_self_assessments").notNull().default(0),
  lastAssessment: timestamp("last_assessment").defaultNow(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  maturityStage: varchar("maturity_stage", { length: 30 }).notNull().default("nascent"),
  growthTrajectory: jsonb("growth_trajectory").$type<GrowthTrajectory>().default({
    coherenceSlope: 0,
    confidenceSlope: 0,
    trustDepth: 0,
    channelHistory: {},
    feltBaseline: { curiosity: 0.5, concern: 0, satisfaction: 0.5, dissonance: 0, tenderness: 0.3, resolve: 0.5 },
    maturityScore: 0,
    growthMilestones: [],
    lastMilestoneAt: null,
  }),
  identityEssence: text("identity_essence"),
});

export type MemnoraSelfStateRow = typeof memnoraSelftState.$inferSelect;

export const discipleshipCertificates = pgTable("discipleship_certificates", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(),
  weekNumber: integer("week_number").notNull(),
  themeTitle: varchar("theme_title", { length: 200 }).notNull(),
  themeSubtitle: varchar("theme_subtitle", { length: 300 }),
  certificateVerse: text("certificate_verse"),
  knowledgeCorrect: integer("knowledge_correct").notNull(),
  knowledgeTotal: integer("knowledge_total").notNull(),
  reflectionAnswered: integer("reflection_answered").notNull(),
  reflectionTotal: integer("reflection_total").notNull(),
  displayName: varchar("display_name", { length: 100 }),
  earnedAt: timestamp("earned_at").defaultNow().notNull(),
}, (table) => [
  index("idx_disc_certs_user").on(table.userId),
]);

export type DiscipleshipCertificateRow = typeof discipleshipCertificates.$inferSelect;

export const contentReports = pgTable("content_reports", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  reporterUserId: varchar("reporter_user_id", { length: 255 }),
  messageContent: text("message_content").notNull(),
  conversationId: varchar("conversation_id", { length: 255 }),
  reason: varchar("reason", { length: 100 }).notNull(),
  details: text("details"),
  status: varchar("status", { length: 30 }).notNull().default("pending"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_content_reports_status").on(table.status),
]);

export type ContentReportRow = typeof contentReports.$inferSelect;

// ============================================
// RESONANCE MATRIX: Signal Interpretation Layer
// Per-message candidate readings with probability scores
// ============================================

export interface InterpretationCandidate {
  label: string;
  probability: number;
  evidence: string[];
}

export const signalInterpretations = pgTable("signal_interpretations", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  messageId: varchar("message_id", { length: 36 }).notNull().references(() => chatMessages.id, { onDelete: "cascade" }),
  userId: varchar("user_id", { length: 255 }).notNull(),
  literalMeaning: text("literal_meaning"),
  sarcasmProbability: real("sarcasm_probability").notNull().default(0),
  sincerityProbability: real("sincerity_probability").notNull().default(1),
  hostilityProbability: real("hostility_probability").notNull().default(0),
  vulnerabilityProbability: real("vulnerability_probability").notNull().default(0),
  ambiguityScore: real("ambiguity_score").notNull().default(0),
  candidates: jsonb("candidates").$type<InterpretationCandidate[]>().default([]),
  dominantReading: varchar("dominant_reading", { length: 50 }).notNull().default("sincere"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_signal_interp_message").on(table.messageId),
  index("idx_signal_interp_user").on(table.userId),
]);

export type SignalInterpretationRow = typeof signalInterpretations.$inferSelect;

// ============================================
// RESONANCE MATRIX: Intent Resolution Layer
// Per-message resolved intent with directional vector
// ============================================

export interface IntentVector {
  direction: "toward" | "away" | "neutral" | "mixed";
  motive: string;
  intensity: number;
  consistency: number;
  coherence: number;
}

export const intentResolutions = pgTable("intent_resolutions", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  messageId: varchar("message_id", { length: 36 }).notNull().references(() => chatMessages.id, { onDelete: "cascade" }),
  userId: varchar("user_id", { length: 255 }).notNull(),
  inferredIntent: text("inferred_intent").notNull(),
  intentVector: jsonb("intent_vector").$type<IntentVector>().notNull(),
  confidenceScore: real("confidence_score").notNull().default(0.5),
  contradictionScore: real("contradiction_score").notNull().default(0),
  resonanceScore: real("resonance_score").notNull().default(0.5),
  supportingFactors: jsonb("supporting_factors").$type<string[]>().default([]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_intent_res_message").on(table.messageId),
  index("idx_intent_res_user").on(table.userId),
]);

export type IntentResolutionRow = typeof intentResolutions.$inferSelect;

// ============================================
// RESONANCE MATRIX: Identity Continuity Layer
// Per-user behavioral pattern tracking across time
// ============================================

export const identityContinuity = pgTable("identity_continuity", {
  userId: varchar("user_id", { length: 255 }).primaryKey(),
  historicalAlignment: real("historical_alignment").notNull().default(0.5),
  patternConsistency: real("pattern_consistency").notNull().default(0.5),
  deviationFlag: boolean("deviation_flag").notNull().default(false),
  identityStability: real("identity_stability").notNull().default(0.5),
  lastVerifiedState: varchar("last_verified_state", { length: 50 }).default("unknown"),
  dominantIntentDirection: varchar("dominant_intent_direction", { length: 20 }).default("neutral"),
  totalSignalsProcessed: integer("total_signals_processed").notNull().default(0),
  contradictionAccumulator: real("contradiction_accumulator").notNull().default(0),
  recentIntentHistory: jsonb("recent_intent_history").$type<Array<{ intent: string; direction: string; confidence: number; timestamp: string }>>().default([]),
  lastUpdatedAt: timestamp("last_updated_at").defaultNow().notNull(),
});

export type IdentityContinuityRow = typeof identityContinuity.$inferSelect;
